import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button as MuiButton, IconButton as MuiIconButton } from "@mui/material";

/**
 * 프로젝트 공통 Button
 *
 * guard="auto"     : Promise를 반환하는 클릭만 완료될 때까지 차단 (기본값)
 * guard="none"     : 즉시 실행 (탭, 페이지 이동, 미디어 제어)
 * guard="throttle" : 같은 버튼의 연속 클릭만 일정 시간 차단
 * guard="request"  : onClick이 반환한 Promise가 끝날 때까지 버튼 차단
 */
function Button({
	name = "", // string
	children, // node
	classes, // object
	color = "primary", // 'inherit' | 'primary'| 'secondary'| 'success'| 'error'| 'info'| 'warning'
	component, // elementType
	disabled = false, // bool
	disableElevation = false, // bool
	disableFocusRipple = false, // bool
	disableRipple = false, // bool
	endIcon, // node
	fullWidth = false, // bool
	href = "", // string
	size = "medium", // 'small' | 'medium'| 'large'
	startIcon, // node
	sx, // object
	variant = "contained", // 'contained' | 'outlined'| 'text'
	onClick = () => {},
	guard = "auto", // 'auto' | 'none' | 'throttle' | 'request'
	throttleMs = 1000,
	loading = false,
	unstyled = false,
	iconButton = false,
	onKeyUp = (e) => {},
	onKeyDown = (e) => {},
	onMouseUp = () => {},
	onMouseOver = () => {},
	onMouseDown = () => {},
	onMouseOut = () => {},
	style,
	className = '',
	...buttonProps
}) {
	const [isGuarding, setIsGuarding] = useState(false);
	const lastClickAtRef = useRef(0);
	const timeoutRef = useRef(null);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, []);

	const ClickButton = useCallback(
		async (e) => {
			if (disabled || loading || isGuarding) return;

			if (guard === "throttle") {
				const now = Date.now();
				if (now - lastClickAtRef.current < throttleMs) return;

				lastClickAtRef.current = now;
				setIsGuarding(true);
				timeoutRef.current = setTimeout(() => {
					if (mountedRef.current) setIsGuarding(false);
				}, throttleMs);
				onClick(e);
				return;
			}

			if (guard === "request") {
				setIsGuarding(true);
				try {
					await Promise.resolve(onClick(e));
				} finally {
					if (mountedRef.current) setIsGuarding(false);
				}
				return;
			}

			const result = onClick(e);
			if (guard === "auto" && typeof result?.then === "function") {
				setIsGuarding(true);
				try {
					await result;
				} finally {
					if (mountedRef.current) setIsGuarding(false);
				}
			}
		},
		[disabled, guard, isGuarding, loading, onClick, throttleMs]
	);
	const KeyUpButton = (e) => {
		onKeyUp(e);
	};
	const KeyDownButton = (e) => {
		onKeyDown(e);
	};
	const MouseUp = () => {
		onMouseUp();
	};
	const MouseOver = () => {
		onMouseOver();
	};
	const MouseDown = () => {
		onMouseDown();
	};
	const MouseOut = () => {
		onMouseOut();
	};

	const Component = unstyled ? "button" : iconButton ? MuiIconButton : MuiButton;
	const isLoading = loading || isGuarding;
	const buttonClassName = [className, isLoading ? 'button-guard-loading' : '']
		.filter(Boolean)
		.join(' ');

	return (
		<Component
			{...buttonProps}
			className={buttonClassName}
			classes={classes}
			color={color}
			component={component}
			disabled={disabled || isLoading}
			disableElevation={disableElevation}
			disableFocusRipple={disableFocusRipple}
			disableRipple={disableRipple}
			endIcon={endIcon}
			fullWidth={fullWidth}
			href={href}
			size={size}
			startIcon={startIcon}
			sx={sx}
			variant={variant}
			onClick={ClickButton}
			onKeyUp={KeyUpButton}
			onKeyDown={KeyDownButton}
			onMouseUp={MouseUp}
			onMouseOver={MouseOver}
			onMouseDown={MouseDown}
			onMouseOut={MouseOut}
			style={unstyled ? style : { textTransform: "none", ...style }}
		>
			{children ?? name}
		</Component>
	);
}

export default React.memo(Button);
